'use client';

import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  AlertTriangle,
  Bell,
  Building2,
  Calendar,
  Check,
  Search,
  Settings,
  Sparkles,
} from 'lucide-react';
import { SiShopify } from 'react-icons/si';
import BookDemoModal from '@/components/ui/BookDemoModal';
import SplitReveal from '@/components/motion/SplitReveal';
import Eyebrow from '@/components/ui/Eyebrow';

/**
 * The AI workspace, shown as the product rather than described: each panel is
 * a working fragment of the feature it names. Panels rise in on a stagger;
 * the few loops (scan line, sync dashes, live dots) are `motion-safe` only.
 */

const LINE = 'border-secondary-dark/[0.07]';
const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function Panel({ title, desc, className = '', children }) {
  return (
    <motion.div
      variants={reveal}
      className={`flex flex-col rounded-2xl border bg-white p-5 ${LINE} ${className}`}
    >
      <h4 className="text-[15px] font-semibold text-secondary-dark">{title}</h4>
      <p className="mt-1 text-[13px] leading-snug text-secondary-dark/55">{desc}</p>
      <div className="mt-4 flex min-h-0 flex-1 flex-col justify-end">{children}</div>
    </motion.div>
  );
}

function Mono({ children, className = '' }) {
  return (
    <span className={`font-mono text-[10px] uppercase tracking-[0.1em] ${className}`}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  AI prescription processing — read, validated, and one caught error */
/* ------------------------------------------------------------------ */

const READS = [
  { id: '#4821', hand: '−2.25  −0.50 ×180', parsed: 'SPH −2.25 · CYL −0.50 · AX 180', ok: true },
  { id: '#4822', hand: '+1.00  −1.25 ×90', parsed: 'SPH +1.00 · CYL −1.25 · AX 90', ok: true },
  { id: '#4823', hand: '−3.50  −0.75 ×', parsed: 'SPH −3.50 · CYL −0.75 · AX —', ok: false },
];

function RxReads() {
  return (
    <div className="relative overflow-hidden rounded-xl bg-[#f5f8fc] p-2">
      <div className="space-y-1.5">
        {READS.map((r) => (
          <div
            key={r.id}
            className={`flex items-center gap-3 rounded-lg border bg-white px-3 py-2 ${r.ok ? LINE : 'border-amber-300'}`}
          >
            <span className="w-10 shrink-0 font-mono text-[11px] font-semibold text-primary-blue">
              {r.id}
            </span>
            <span className="hidden w-[132px] shrink-0 whitespace-pre font-hand text-[17px] leading-none text-[#1d3a8a] sm:block">
              {r.hand}
            </span>
            <span className="hidden text-secondary-dark/25 sm:block">→</span>
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-secondary-dark/75">
              {r.parsed}
            </span>
            {r.ok ? (
              <span className="grid size-[18px] shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                <Check className="size-3" strokeWidth={3.5} />
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-amber-600">
                <AlertTriangle className="size-3.5" strokeWidth={2.25} />
                Axis missing
              </span>
            )}
          </div>
        ))}
      </div>
      {/* the scan that never stops */}
      <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-10 motion-safe:block motion-safe:animate-sweep">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-blue/0 to-primary-blue/15" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-primary-blue/70" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Performance analytics                                              */
/* ------------------------------------------------------------------ */

const BARS = [42, 58, 50, 72, 64, 86, 70, 90, 78, 96];

function Bars() {
  const reduce = useReducedMotion();
  return (
    <>
      <div className="flex gap-1.5">
        {['Processing time', 'Order accuracy', 'Completion rate'].map((m, i) => (
          <span
            key={m}
            className={`rounded-md px-2 py-1 font-mono text-[9.5px] uppercase tracking-[0.06em] ${
              i === 0 ? 'bg-ink text-white' : 'text-secondary-dark/45'
            }`}
          >
            {m}
          </span>
        ))}
      </div>
      <div className="mt-3 flex h-24 items-end gap-1.5 border-b border-secondary-dark/10">
        {BARS.map((h, i) => (
          <motion.div
            key={i}
            className={`flex-1 rounded-t-[3px] ${i >= BARS.length - 2 ? 'bg-primary-blue' : 'bg-primary-blue/25'}`}
            initial={reduce ? false : { height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.2 + i * 0.05 }}
          />
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Orders, labs, Shopify                                              */
/* ------------------------------------------------------------------ */

const ORDERS = [
  { id: '#4821', who: 'E. Collins', status: 'In lab', tone: 'text-primary-blue' },
  { id: '#4820', who: 'J. Alvarez', status: 'Ready', tone: 'text-emerald-600' },
  { id: '#4819', who: 'M. Tan', status: 'Shipped', tone: 'text-secondary-dark/45' },
];
const LABS = ['Sunshine', 'Milligan', 'Mission Optics', 'PROptics'];
const SYNCS = ['Prescription orders', 'Fulfillment', 'Inventory'];

function Rows({ children }) {
  return (
    <div className={`divide-y divide-secondary-dark/[0.07] rounded-xl border ${LINE}`}>
      {children}
    </div>
  );
}

const STAGES = ['Purchased', 'Recognized', 'Processed', 'Sent to lab', 'Manufacturing', 'Shipping'];
const AT = 4; // the stage the sample order is in

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export default function AiSolutionSection() {
  const [demoOpen, setDemoOpen] = useState(false);
  const reduce = useReducedMotion();

  const openDemo = () => {
    if (typeof window !== 'undefined' && window.gtag_report_conversion) {
      window.gtag_report_conversion();
    }
    setDemoOpen(true);
  };

  return (
    <section id="lensync-ai" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute -left-28 top-24 size-80 rounded-full bg-primary-blue/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 size-80 rounded-full bg-emerald-200/30 blur-3xl" />

      <div className="container relative mx-auto px-5">
        {/* intro */}
        <div className="mx-auto max-w-4xl text-center">
          <Eyebrow className="mb-5">Our AI Solution</Eyebrow>
          <SplitReveal className="display text-[clamp(34px,4.4vw,64px)] text-secondary-dark">
            Meet AI CRM for Optical Business
          </SplitReveal>
          <p className="mx-auto mt-6 max-w-xl text-lg text-gray-500">
            LenSync AI is an intelligent platform that transforms optical retail and lab operations
            — prescription recognition, order management, and real-time tracking in one place.
          </p>
        </div>

        {/* app window */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={reveal}
          className="mx-auto mt-14 max-w-6xl overflow-hidden rounded-[1.75rem] border border-white bg-[#f5f8fc]/80 shadow-[0_40px_120px_-30px_rgba(2,32,71,0.3)] backdrop-blur"
        >
          {/* chrome */}
          <div
            className={`flex items-center justify-between border-b bg-white px-5 py-3.5 md:px-6 ${LINE}`}
          >
            <div className="flex items-center gap-2.5">
              <span className="grid size-7 place-items-center rounded-lg bg-primary-blue text-white">
                <Sparkles className="size-4" strokeWidth={2.5} />
              </span>
              <span className="text-sm font-semibold tracking-tight text-secondary-dark">
                LenSync AI
              </span>
              <span className="text-secondary-dark/20">/</span>
              <span className="text-sm text-secondary-dark/55">Order workspace</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="hidden items-center gap-3 text-secondary-dark/35 sm:flex">
                <Search className="size-4" strokeWidth={2} />
                <Bell className="size-4" strokeWidth={2} />
                <Settings className="size-4" strokeWidth={2} />
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1 font-mono text-[11px] font-medium text-white">
                <span className="size-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse" />
                Live
              </span>
            </div>
          </div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            transition={{ staggerChildren: 0.07 }}
            className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 md:gap-4 md:p-5 lg:grid-cols-12"
          >
            <Panel
              className="sm:col-span-2 lg:col-span-7"
              title="AI Prescription Processing"
              desc="Automated recognition, validation, and error checking"
            >
              <RxReads />
            </Panel>

            <Panel
              className="sm:col-span-2 lg:col-span-5"
              title="Performance Analytics"
              desc="Track processing times, order accuracy, and completion rates"
            >
              <Bars />
            </Panel>

            <Panel
              className="lg:col-span-4"
              title="Smart Order Management"
              desc="Centralized CRM for all prescription orders"
            >
              <Rows>
                {ORDERS.map((o) => (
                  <div key={o.id} className="flex items-center gap-3 px-3 py-2">
                    <span className="font-mono text-[11px] font-semibold text-secondary-dark/45">
                      {o.id}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-secondary-dark">
                      {o.who}
                    </span>
                    <Mono className={`font-semibold ${o.tone}`}>{o.status}</Mono>
                  </div>
                ))}
              </Rows>
            </Panel>

            <Panel
              className="lg:col-span-4"
              title="Lab Integration"
              desc="Direct connection with optical labs for seamless processing"
            >
              <Rows>
                {LABS.map((l, i) => (
                  <div key={l} className="flex items-center gap-3 px-3 py-2">
                    <Building2 className="size-3.5 text-secondary-dark/35" strokeWidth={2} />
                    <span className="min-w-0 flex-1 truncate text-xs text-secondary-dark">{l}</span>
                    <span
                      className="size-1.5 rounded-full bg-emerald-500 motion-safe:animate-pulse"
                      style={{ animationDelay: `${i * 0.4}s` }}
                    />
                    <Mono className="text-secondary-dark/45">Connected</Mono>
                  </div>
                ))}
              </Rows>
            </Panel>

            <Panel
              className="sm:col-span-2 lg:col-span-4"
              title="Fast & Efficient Shopify Integration"
              desc="Instant prescription order sync, automated fulfillment, and real-time inventory updates"
            >
              <div className="flex items-center gap-2">
                <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-[#e7f8ef] text-lg text-[#008060]">
                  <SiShopify />
                </span>
                <svg viewBox="0 0 100 8" preserveAspectRatio="none" className="h-2 min-w-0 flex-1">
                  <path
                    d="M0 4H100"
                    stroke="#01a7ff"
                    strokeWidth="2"
                    strokeDasharray="4 6"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    className="motion-safe:animate-dash"
                  />
                </svg>
                <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary-blue text-white">
                  <Sparkles className="size-4" strokeWidth={2.5} />
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {SYNCS.map((s) => (
                  <span
                    key={s}
                    className="flex items-center gap-1.5 text-xs text-secondary-dark/70"
                  >
                    <Check className="size-3.5 text-emerald-500" strokeWidth={3} />
                    {s}
                  </span>
                ))}
              </div>
            </Panel>

            {/* Status tracking — the order tour above plays it; here it is the way in */}
            <motion.div
              variants={reveal}
              className="flex flex-col gap-5 rounded-2xl bg-ink p-5 text-white sm:col-span-2 md:flex-row md:items-center md:gap-8 lg:col-span-12"
            >
              <div className="shrink-0">
                <h4 className="text-[15px] font-semibold">Status Tracking</h4>
                <p className="mt-1 text-[13px] text-white/55">
                  Real-time updates from production to delivery
                </p>
              </div>

              <div className="relative min-w-0 flex-1">
                <div className="absolute inset-x-[8.33%] top-[5px] h-px bg-white/15">
                  <motion.div
                    className="h-full origin-left bg-primary-blue"
                    initial={reduce ? false : { scaleX: 0 }}
                    whileInView={{ scaleX: AT / (STAGES.length - 1) }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
                  />
                </div>
                <div className="relative grid grid-cols-6">
                  {STAGES.map((s, i) => (
                    <div key={s} className="flex flex-col items-center gap-2">
                      <span
                        className={`size-[11px] rounded-full border-2 border-ink ${
                          i < AT
                            ? 'bg-primary-blue'
                            : i === AT
                              ? 'bg-white ring-2 ring-primary-blue motion-safe:animate-pulse'
                              : 'bg-white/25'
                        }`}
                      />
                      <Mono
                        className={`hidden text-center text-[9px] sm:block ${i <= AT ? 'text-white/80' : 'text-white/35'}`}
                      >
                        {s}
                      </Mono>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={openDemo}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
              >
                <Calendar className="size-4" strokeWidth={2} />
                Schedule Demo
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
